import { gql } from '@apollo/client';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
  FormLabel,
} from '@apollo/orbit';
import { Select } from '@apollo/space-kit/Select';
import React from 'react';
import { SelectChangeEvent } from 'typings/events/selection-change-event';

import { InsightsLineChartTooltip } from 'src/app/graph/insights/InsightsLineChartTooltip';
import { LineChart } from 'src/components/charts/LineChart';
import { Loading } from 'src/components/common/loading/Loading';
import { DateRangeBadge } from 'src/components/dateRangeBadge/DateRangeBadge';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import {
  FetchesOverTimeResultFragment,
  Resolution,
} from 'src/lib/graphqlTypes/types';

import {
  FetchRequestsMetricType,
  useFetchRequestsChartStacks,
} from './useFetchRequestsChartStacks';

export const fetchesOverTimeResultFragment = gql`
  fragment FetchesOverTimeResultFragment on SubgraphInsightsTimeseriesResult {
    roundedUpTo
    roundedDownFrom
    records {
      timestamp
      data {
        fetchCount
        fetchCountPerMin
      }
    }
  }
`;

function getChartDescription(metricType: FetchRequestsMetricType) {
  switch (metricType) {
    case 'fetchCountPerMin':
      return 'The rate per minute of fetches to this subgraph.';
    case 'fetchCount':
      return 'The total number of fetches to this subgraph.';
    default:
      assertUnreachable(metricType);
  }
}

function getChartTitle(metricType: FetchRequestsMetricType) {
  switch (metricType) {
    case 'fetchCountPerMin':
      return 'Fetch Rate';
    case 'fetchCount':
      return 'Fetch Count';
    default:
      assertUnreachable(metricType);
  }
}

interface FetchesOverTimeHeadingProps {
  metricType: FetchRequestsMetricType;
  onFetchMetricTypeChange: (
    event: SelectChangeEvent<FetchRequestsMetricType>,
  ) => void;
}

const FetchesOverTimeHeading = ({
  metricType,
  onFetchMetricTypeChange,
}: FetchesOverTimeHeadingProps) => {
  return (
    <div className="flex items-center gap-2">
      <FormControl>
        <FormLabel className="sr-only">Fetch Metric Type</FormLabel>
        <Select
          value={metricType}
          onChange={onFetchMetricTypeChange}
          feel="flat"
          className="self-center font-heading text-xl font-medium text-heading"
          placement="bottom-start"
        >
          <option className="font-normal" value="fetchCount">
            Fetch count
          </option>
          <option className="font-normal" value="fetchCountPerMin">
            Fetch rate
          </option>
        </Select>
      </FormControl>
      <DateRangeBadge />
    </div>
  );
};

interface Props {
  className?: string;
  loading: boolean;
  result?: FetchesOverTimeResultFragment;
  resolution: Resolution;
}

export function FetchesOverTime({
  className,
  loading,
  result,
  resolution,
}: Props) {
  const [fetchMetricType, setFetchMetricType] =
    React.useState<FetchRequestsMetricType>('fetchCount');

  const onFetchMetricTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setFetchMetricType(event.target.value as FetchRequestsMetricType);
  };
  const { fetchRequestsChartStackInfo } = useFetchRequestsChartStacks({
    result,
    metricType: fetchMetricType,
    resolution,
  });
  return (
    <Card variant="unstyled" className={className}>
      <CardHeader>
        <CardTitle>
          <div className="flex items-center">
            <FetchesOverTimeHeading
              metricType={fetchMetricType}
              onFetchMetricTypeChange={onFetchMetricTypeChange}
            />
          </div>
        </CardTitle>
        <CardDescription className="pl-3">
          {getChartDescription(fetchMetricType)}
        </CardDescription>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="flex justify-center py-10 text-center">
            <Loading />
          </div>
        ) : (
          <LineChart
            stacked
            stacks={fetchRequestsChartStackInfo.stacks}
            className="h-56"
            resolution={resolution}
            allowSingleXAxisValue
            data-testid="subgraph-fetches-line-chart"
            tooltipLabelAs={
              <InsightsLineChartTooltip
                title={getChartTitle(fetchMetricType)}
              />
            }
            hideCursorArrow
          />
        )}
      </CardBody>
    </Card>
  );
}
